import React, { useState } from 'react';
import { Icons } from '../Icon.jsx';

const FieldGroup = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-slate-200 rounded bg-slate-50/50">
      <button
        className="w-full flex items-center justify-between p-2 text-[10px] font-semibold text-slate-600 uppercase tracking-wider hover:bg-slate-100/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{title}</span>
        {isOpen ? <Icons.ChevronDown className="w-3 h-3" /> : <Icons.ChevronRight className="w-3 h-3" />}
      </button>
      {isOpen && <div className="p-2 space-y-2 bg-white/30">{children}</div>}
    </div>
  );
};

const BehaviorItem = ({ comp, def, onRemove, onPropChange, renderField, onCopy, onToggleEnable }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isEnabled = comp.props.enabled !== false;

  return (
    <div className={`bg-admin_canvas border-b border-admin_bordered last:border-b-0 ${isEnabled ? '' : 'opacity-60'}`}>
      <div 
        className="bg-admin_neutral_1/10 px-2 py-2 flex justify-between items-center cursor-pointer hover:bg-admin_neutral_1/20 transition-colors group" 
        onClick={() => setIsOpen(!isOpen)}
      >
          <div className="flex items-center gap-2 text-xs font-semibold text-admin_text select-none">
             {isOpen ? <Icons.ChevronDown className="w-3 h-3 text-gray-400" /> : <Icons.ChevronRight className="w-3 h-3 text-gray-400" />}
             {def.icon && Icons[def.icon] && React.createElement(Icons[def.icon], {className:"w-3 h-3 text-blue-500"})} 
             <span className={isEnabled ? '' : 'line-through text-gray-400'}>{def.label}</span>
          </div>
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={(e) => { e.stopPropagation(); onToggleEnable(comp.id, !isEnabled); }} 
                className={`p-1 rounded hover:bg-slate-300 mr-1 ${isEnabled ? 'text-blue-500' : 'text-gray-400'}`}
                title={isEnabled ? "Disable Behavior" : "Enable Behavior"}
              >
                {isEnabled ? <Icons.Eye className="w-3 h-3"/> : <Icons.EyeOff className="w-3 h-3"/>}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onCopy(comp); }} 
                className="text-gray-400 hover:text-blue-500 p-1 rounded hover:bg-slate-300 mr-1"
                title="Copy Behavior Settings"
              >
                <Icons.Copy className="w-3 h-3"/>
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onRemove(comp.id); }} 
                className="text-gray-400 hover:text-red-500 p-1 rounded hover:bg-slate-300"
                title="Remove Behavior"
              >
                <Icons.Remove className="w-3 h-3"/>
              </button>
          </div>
      </div>
      {isOpen && isEnabled && (
        <div className="p-3 space-y-2 bg-white/50">
            {def.fields.map((f, idx) => {
                // Check if this is a group
                if (f.type === 'group') {
                    return (
                        <FieldGroup
                            key={f.name || `group-${idx}`}
                            title={f.label}
                            defaultOpen={f.defaultOpen ?? false}
                        >
                            {(f.fields || []).map(subField => (
                                <div key={subField.name}>
                                    {renderField(
                                        subField.label,
                                        comp.props[subField.name],
                                        (v) => onPropChange(comp.id, subField.name, v),
                                        subField.type,
                                        subField.options,
                                        subField.name,
                                        comp.id,
                                        subField.step,
                                        def.id
                                    )}
                                </div>
                            ))}
                        </FieldGroup>
                    );
                }

                // Regular field
                return (
                    <div key={f.name}>
                        {renderField(f.label, comp.props[f.name], (v) => onPropChange(comp.id, f.name, v), f.type, f.options, f.name, comp.id, f.step, def.id)}
                    </div>
                );
            })}
        </div>
      )}
    </div>
  );
};

export default BehaviorItem;
